import { App } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker, useLocation, useParams } from 'react-router';

import { consumeOverlayBackBypass, isAuthOverlayOpen } from '@/pages/Auth';
import useAppNavigate from '@/shared/hooks/useAppNavigation';
import { ROUTES } from '@/shared/routes';
import MainLoader from '@/shared/ui/main-loader';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  resetTabBar,
  setTabBarConfig,
  setTabBarVisible
} from '@/store/ui.slice';

import {
  HOST_PAGE_ROUTES,
  SERVICE_ROUTES,
  type TServiceId,
  buildServiceUrl,
  getServiceConfig
} from './config';
import {
  type IBridgeHostDeps,
  type TLocalActionHandler,
  createDefaultHostDeps,
  setTabBarPressHandler,
  useIframeBridge
} from './host';
import {
  BRIDGE_ERROR_CODES,
  type IInitContext,
  PROTOCOL_VERSION,
  SYSTEM_EVENTS
} from './protocol';
import { type IRouteSyncPayload, ROUTE_SYNC } from './route-sync';

/**
 * Кросс-ориджин iframe НЕ отдаёт надёжного onError при сбое загрузки (неверный адрес, DNS,
 * недоступность): событие error для cross-origin почти не срабатывает, а onLoad может прийти
 * от браузерной error-страницы. Единственный достоверный сигнал «сервис жив» — bridge-хендшейк
 * (bridge.ready → onReady). Если за это время хендшейка нет — считаем загрузку неудавшейся.
 */
const SERVICE_LOAD_TIMEOUT_MS = 15000;

export interface IIframeBridgeProps {
  /** Какой сервис монтируем (определяет URL/origin из конфига моста). */
  serviceId: TServiceId;
  /** Зависимости хоста (auth/sso/navigation/...), к которым мост маппит системные действия. */
  deps?: IBridgeHostDeps;
  /** Доменные действия сервиса (не системные). */
  onLocalAction?: TLocalActionHandler;
  /** Доп. данные для init.webProperties. */
  webProperties?: Record<string, unknown>;
  className?: string;
}

const IframeBridge: React.FC<IIframeBridgeProps> = ({
  serviceId,
  deps = {},
  onLocalAction,
  webProperties,
  className
}) => {
  const { i18n, t } = useTranslation();
  const locale = i18n.language.slice(0, 2);
  const mode = useAppSelector((state) => state.app.theme);
  const isAuthorized = useAppSelector((state) => state.auth.isAuthorized);
  const { navigate } = useAppNavigate();
  const dispatch = useAppDispatch();
  const { message } = App.useApp();

  // Стартовый внутренний путь сервиса. splat = хвост `/:lang/<service>/*`, search — query.
  // Используется ТОЛЬКО для initialPath на маунте: адресную строку с роутами сервиса не
  // синхронизируем (отказались от sync в пользу canGoBack-модели нативки).
  const splat = useParams()['*'] ?? '';
  const { search } = useLocation();

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [ready, setReady] = useState(false);
  // Зеркало ready в ref — читается из mount-таймера загрузки (без пересборки таймера на ререндере).
  const readyRef = useRef(false);

  const [isLoading, setIsLoading] = useState(true);
  const loading = isLoading || !ready;

  const config = getServiceConfig(serviceId);

  // initialPath передаём ТОЛЬКО когда оболочка указывает на внутренний путь, отличный от
  // базового (паритет с нативкой: route.params.initialPath обычно undefined → сервис при старте
  // не навигирует и не дёргает sso). На базовом входе splat пуст / совпадает с basePath → undefined.
  const initialPath = useMemo(() => {
    const servicePath = `/${splat}${search}`;
    return splat && servicePath !== config.basePath ? servicePath : undefined;
  }, []);

  // src фиксируется на маунте: только origin + locale (паритет с нативкой — стартовый путь
  // отдаём через webProperties.initialPath, сервис сам туда навигирует). src не меняется →
  // iframe не перезагружается при смене своего внутреннего роута.
  const [src] = useState(() => buildServiceUrl(config.url, locale));

  // canGoBack-модель (как нативный canGoBackRef): сервис через navigation.setCanGoBack сообщает,
  // есть ли у него внутренняя история. На браузерный «назад» (POP) смотрим сюда: есть история —
  // отдаём сервису back.pressed и остаёмся; нет — отпускаем POP (выход на входной путь).
  const canGoBackRef = useRef(false);
  // Одноразовый пропуск блокера для service-initiated goBack (navigation.back/close через navigate),
  // чтобы собственный navigate(-1) не перехватился как внутренний «назад» сервиса.
  const bypassBlockerRef = useRef(false);

  // Ref на sendEvent: onReady не может ссылаться на sendEvent напрямую (он возвращается
  // этим же хуком ниже) — разрываем цикл через ref, заполняемый в эффекте.
  const sendEventRef = useRef<
    ((event: string, payload: unknown, correlationId?: string) => void) | null
  >(null);

  const handleReady = () => {
    const context: IInitContext = {
      protocolVersion: PROTOCOL_VERSION,
      miniApp: { id: serviceId },
      platform: 'web',
      mode,
      theme: {}, // TODO: нужно удалить, так как токены цветов у сервисов свои
      locale,
      insets: { top: 0, bottom: 0, left: 0, right: 0 },
      featureFlags: {},
      auth: { isAuthenticated: isAuthorized },
      // initialPath кладём только если он есть (как нативный webProperties={initialPath?{…}:undefined}).
      webProperties: {
        ...(initialPath ? { initialPath } : {}),
        ...webProperties
      }
    };
    sendEventRef.current?.(SYSTEM_EVENTS.Init, context);
    readyRef.current = true;
    setReady(true);
  };

  // Сервис → оболочка: routeChanged больше не зеркалим в URL (отказ от sync адресной строки) —
  // просто подтверждаем приём. Остальные доменные действия отдаём в onLocalAction.
  const handleLocalAction: TLocalActionHandler = (message, respond, fail) => {
    if (message.action === ROUTE_SYNC.routeChanged) {
      const path = (message.payload as IRouteSyncPayload | null)?.path ?? '';
      const canonical = path.startsWith('/') ? path : `/${path}`;
      navigate(`/${SERVICE_ROUTES[serviceId]}${canonical}`, { replace: true });
      respond(undefined);
      return;
    }
    if (onLocalAction) onLocalAction(message, respond, fail);
    else
      fail({
        code: BRIDGE_ERROR_CODES.UNKNOWN_ACTION,
        message: message.action
      });
  };

  // Дефолтные deps (ленивая авторизация через openAuth + SSO) + управление таб-баром оболочки
  // (сервис задаёт свои табы через ui.setTabBar/ui.setTabBarVisible) + переопределения из пропа.
  const hostDeps: IBridgeHostDeps = {
    ...createDefaultHostDeps(serviceId),
    navigation: {
      // Закрытие сервиса = выход из webview-обёртки на главную (PUSH, блокером не перехватывается).
      close: () => navigate(`/${ROUTES.home}`),
      // Явный goBack сервиса = выход на входной путь; помечаем пропуск, чтобы POP не ушёл в
      // back.pressed (это не внутренний «назад» сервиса, а просьба выйти).
      goBack: () => {
        bypassBlockerRef.current = true;
        navigate(-1);
      },
      navigate: (route, params) => {
        navigate(route, { state: params });
      },
      openMiniApp: (miniAppId, params) => {
        navigate(`/${miniAppId}`, { state: params });
      },
      // PUSH на страницу оболочки: iframe размонтируется, «назад» вернёт на входной путь
      // сервиса, и он стартует заново (в нативке экран открывается поверх живого WebView).
      openHostPage: (page) => {
        navigate(`/${HOST_PAGE_ROUTES[page]}`);
      }
    },
    // Причина закрытия (и прочие уведомления сервиса) → тост AntD. Рендерится в портале на
    // уровне App (index.tsx), поэтому переживает уход с роута при close. warn → warning.
    notify: (type, text) => {
      const show =
        type === 'success'
          ? message.success
          : type === 'error'
            ? message.error
            : type === 'warn'
              ? message.warning
              : message.info;
      show(text);
    },
    chrome: {
      setTabBar: (cfg) => dispatch(setTabBarConfig(cfg)),
      setTabBarVisible: (value) => dispatch(setTabBarVisible(value)),
      // Сервис сообщает наличие внутренней истории — на неё смотрит блокер «назад».
      setCanGoBack: (value) => {
        canGoBackRef.current = value;
      }
    },
    ...deps
  };

  const { sendEvent } = useIframeBridge({
    iframeRef,
    serviceOrigin: config.origin,
    deps: hostDeps,
    onLocalAction: handleLocalAction,
    onReady: handleReady
  });

  useEffect(() => {
    sendEventRef.current = sendEvent;
  }, [sendEvent]);

  // Таймаут bridge-хендшейка: нет ready за SERVICE_LOAD_TIMEOUT_MS → сервис не загрузился
  // (см. коммент у константы) → тост + уход на главную. Ставится один раз на маунт.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!readyRef.current) {
        message.error(t('errors.iframeError'));
        navigate(`/${ROUTES.home}`);
      }
    }, SERVICE_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  // Перехват браузерного/мобильного «назад»: блокируем POP, пока примонтирован сервис.
  // bypass — одноразовый пропуск для service-initiated выхода (navigation.goBack).
  const blocker = useBlocker(({ historyAction }) => {
    if (historyAction !== 'POP') return false;
    if (bypassBlockerRef.current) {
      bypassBlockerRef.current = false;
      return false;
    }
    // Оверлей авторизации — верхний слой над сервисом: «назад» закрывает его (через popstate),
    // а не идёт в сервис. И служебный POP самого оверлея (снятие его history-записи) пропускаем.
    if (isAuthOverlayOpen()) return false;
    if (consumeOverlayBackBypass()) return false;
    return true;
  });

  // Решение по заблокированному POP: есть внутренняя история → back.pressed + остаёмся (reset,
  // аналог нативного return true); нет → отпускаем POP → выход на входной путь (proceed).
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (canGoBackRef.current) {
      sendEvent(SYSTEM_EVENTS.BackPressed, null);
      blocker.reset();
    } else {
      blocker.proceed();
    }
  }, [blocker, sendEvent]);

  // Клик по service-табу (рисует виджет навигации) возвращаем сервису событием tabBar.pressed.
  // На размонтировании сбрасываем таб-бар к дефолтным табам оболочки.
  useEffect(() => {
    setTabBarPressHandler((id) =>
      sendEvent(SYSTEM_EVENTS.TabBarPressed, { id })
    );
    return () => {
      setTabBarPressHandler(null);
      dispatch(resetTabBar());
    };
  }, [sendEvent, dispatch]);

  // После init держим сервис в синхроне: тема/локаль/авторизация.
  useEffect(() => {
    if (!ready) return;
    sendEvent(SYSTEM_EVENTS.ThemeChanged, mode);
  }, [ready, mode, sendEvent]);

  useEffect(() => {
    if (!ready) return;
    sendEvent(SYSTEM_EVENTS.LocaleChanged, { locale });
  }, [ready, locale, sendEvent]);

  useEffect(() => {
    if (!ready) return;
    sendEvent(SYSTEM_EVENTS.AuthChanged, { isAuthenticated: isAuthorized });
  }, [ready, isAuthorized, sendEvent]);

  return (
    <>
      {loading && <MainLoader fullScreen blur />}
      <iframe
        ref={iframeRef}
        src={src}
        title={serviceId}
        className={className}
        style={{ display: 'block', width: '100%', height: '100dvh', border: 0 }}
        onLoad={() => setIsLoading(false)}
        onError={() => setIsLoading(false)}
      />
    </>
  );
};

export default IframeBridge;
